import { CustomerSuccessOptionsAppserviceService } from '@wealthlane/api-services/wealthlane-customer-service';
import { RolePublishService } from '@wealthlane/api-services/wealthlane-customer-service';
import { safeApiCall } from '@packages/shared/utils';

export type LabelValueOption = { label: string; value: string };

export async function fetchMyCompaniesOptions(
  excludeCompanyId?: string | null,
): Promise<LabelValueOption[]> {
  const data = await safeApiCall(() =>
    CustomerSuccessOptionsAppserviceService.getApiCustomerApiCustomerCustomerSuccessOptionsMyCompanies(),
  );
  return (data ?? [])
    .map((company) => ({
      label: company.name ?? '',
      value: company.id ?? '',
    }))
    .filter((option) => !excludeCompanyId || option.value !== excludeCompanyId);
}

export async function fetchVendorSelfRolesByCompany(
  companyId: string | null | undefined,
): Promise<LabelValueOption[]> {
  if (!companyId) return [];
  const data = await safeApiCall(() =>
    CustomerSuccessOptionsAppserviceService.getApiCustomerApiCustomerCustomerSuccessVendorOptionsSelfRoles(
      companyId,
    ),
  );
  return (data ?? []).map((role) => ({
    label: role.name ?? '',
    value: role.id ?? '',
  }));
}

export async function fetchDestinationCompaniesOptions(): Promise<LabelValueOption[]> {
  const data = await safeApiCall(() =>
    CustomerSuccessOptionsAppserviceService.getApiCustomerApiCustomerCustomerSuccessOptionsAllowedDestinationCompanies(),
  );
  return (data ?? []).map((company) => ({
    label: company.name ?? '',
    value: company.id ?? '',
  }));
}

export type RolePublishRoleOption = LabelValueOption & { identityRoleId: string };

export async function fetchRolePublishRoleOptions(): Promise<RolePublishRoleOption[]> {
  const data = await safeApiCall(() =>
    CustomerSuccessOptionsAppserviceService.getApiCustomerApiCustomerCustomerSuccessOptionsSelfRoles(),
  );
  return (data ?? []).map((item) => ({
    label: item.name ?? '',
    value: item.customerRoleId ?? '',
    identityRoleId: item.id ?? '',
  }));
}

/** Customer-role id on list rows (group header / leaf). API fields vary by endpoint shape. */
export function resolveRolePublishSourceRoleCustomerId(
  row?: Record<string, unknown>,
): string | undefined {
  const id = row?.sourceRoleId ?? row?.customerRoleId ?? row?.roleId;
  return typeof id === 'string' && id.length > 0 ? id : undefined;
}

/**
 * Source role on GET / compose `params` — builder sets `sourceRoleId`; form fetch template only sends `id`
 * (same customer-role id).
 */
export function resolveRolePublishSourceRoleFromParams(
  params?: Record<string, unknown>,
): string | undefined {
  const explicit = params?.sourceRoleId;
  if (typeof explicit === 'string' && explicit.length > 0) return explicit;
  const id = params?.id;
  if (typeof id === 'string' && id.length > 0) return id;
  return undefined;
}

/** Legacy edit uses `publishedRoles` on the flattened list row (group snapshot), not identity-wide refetch alone. */
export function extractRolePublishDestinationCompanyIds(
  row?: Record<string, unknown>,
): string[] {
  const publishedRoles = row?.publishedRoles as Array<{ destinationCompanyId?: string }> | undefined;
  const fromGroup = publishedRoles
    ?.map((item) => item.destinationCompanyId)
    .filter((id): id is string => typeof id === 'string' && id.length > 0);
  if (fromGroup && fromGroup.length > 0) {
    return fromGroup;
  }
  const single = row?.destinationCompanyId;
  if (typeof single === 'string' && single.length > 0) {
    return [single];
  }
  if (Array.isArray(single)) {
    return single.filter((id): id is string => typeof id === 'string' && id.length > 0);
  }
  return [];
}

export function serializeRolePublishDestinationCompanyIds(ids: string[]): string {
  return ids.join(',');
}

export function parseRolePublishDestinationCompanyIds(serialized?: string): string[] | undefined {
  if (!serialized) return undefined;
  const ids = serialized.split(',').map((part) => part.trim()).filter(Boolean);
  return ids.length > 0 ? ids : undefined;
}

/** Grouped table: action menu is on the source-role header — use every visible leaf row. */
export function buildRolePublishActionRowFromGroup(row: {
  leafRows?: Array<{ original?: Record<string, unknown> }>;
  original?: Record<string, unknown>;
}): Record<string, unknown> {
  const leaves = row.leafRows?.map((leaf) => leaf.original).filter(Boolean) as
    | Record<string, unknown>[]
    | undefined;
  if (!leaves?.length) {
    return row.original ?? {};
  }
  const first = leaves[0];
  return {
    ...first,
    publishedRoles: leaves,
  };
}

export async function fetchPublishedDestinationCompanyIdsByRole(
  sourceRoleCustomerId: string | null | undefined,
): Promise<string[]> {
  if (!sourceRoleCustomerId) return [];
  const data = await safeApiCall(() =>
    RolePublishService.getApiCustomerApiV2CustomerCustomerSuccessRolesPublished(
      undefined,
      sourceRoleCustomerId,
      undefined,
      undefined,
      0,
      1000,
    ),
  );
  const destinationCompanyIds: string[] = [];
  data?.items?.forEach((group) => {
    group.publishedRoles?.forEach((publishedRole) => {
      if (publishedRole.destinationCompanyId) {
        destinationCompanyIds.push(publishedRole.destinationCompanyId);
      }
    });
  });
  return destinationCompanyIds;
}

export async function fetchSelfRolesForMapping(): Promise<LabelValueOption[]> {
  const data = await safeApiCall(() =>
    CustomerSuccessOptionsAppserviceService.getApiCustomerApiCustomerCustomerSuccessOptionsSelfRoles(),
  );
  return (data ?? []).map((item) => ({
    label: item.name ?? '',
    value: item.id ?? '',
  }));
}

export type RoleMappingSourceRow = {
  id: string;
  name: string;
  roleName: string;
  roleId: string;
  companyId: string;
};

/** Composite row key used by role-mapping source picker (`companyId....roleId`). */
export function roleMappingItemToRowKey(item: Record<string, unknown>): string {
  const companyId = String(item.sourceCompanyId ?? '');
  const roleId = String(item.sourceRoleId ?? '');
  return `${companyId}....${roleId}`;
}

export function roleMappingSelectedKeysFromItems(
  items: Array<Record<string, unknown>> | undefined,
): string[] {
  return (items ?? []).map(roleMappingItemToRowKey);
}

/** OPEN_COMPOSE_MODAL params key — comma-separated `companyId....roleId` keys. */
export const ROLE_MAPPING_SELECTED_KEYS_PARAM = 'selectedMappingKeys';

export function serializeRoleMappingSelectedKeys(keys: string[]): string {
  return keys.join(',');
}

export function parseRoleMappingSelectedKeys(serialized?: string): string[] {
  if (!serialized) return [];
  return serialized
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

/** Hydrate edit/view form from compose `params` (executeBuilder only receives params + meta). */
export function buildRoleMappingFormDataFromComposeParams(
  params: Record<string, unknown> | undefined,
  sourceRows: RoleMappingSourceRow[],
): Record<string, unknown> | undefined {
  const destinationRoleId = params?.id;
  if (typeof destinationRoleId !== 'string' || destinationRoleId.length === 0) {
    return undefined;
  }
  const keys = parseRoleMappingSelectedKeys(
    typeof params?.[ROLE_MAPPING_SELECTED_KEYS_PARAM] === 'string'
      ? (params[ROLE_MAPPING_SELECTED_KEYS_PARAM] as string)
      : undefined,
  );
  if (keys.length === 0) return undefined;
  return {
    destinationRoleId,
    selectedRoleMappingItems: roleMappingItemsFromSelectedKeys(keys, sourceRows),
  };
}

/** List / grouped-table row → hidden form field `selectedRoleMappingItems`. */
export function normalizeRoleMappingItemFromApi(
  entry: Record<string, unknown>,
): Record<string, unknown> {
  const sourceCompanyId = String(entry.sourceCompanyId ?? entry.companyId ?? '');
  const sourceRoleId = String(entry.sourceRoleId ?? entry.roleId ?? '');
  const sourceCompanyName = entry.sourceCompanyName ?? entry.name;
  const sourceRoleName = entry.sourceRoleName ?? entry.roleName;
  return {
    sourceCompanyId,
    sourceRoleId,
    ...(typeof sourceCompanyName === 'string' ? { sourceCompanyName } : {}),
    ...(typeof sourceRoleName === 'string' ? { sourceRoleName } : {}),
  };
}

/** Build compose form `data` from a flattened allowed-role-mapping table row. */
export function buildRoleMappingFormDataFromTableRow(
  row: Record<string, unknown> | undefined,
  opts?: { destinationRoleId?: string },
): Record<string, unknown> | undefined {
  if (!row) return undefined;
  const destinationRoleId =
    (typeof row.destinationRoleId === 'string' && row.destinationRoleId.length > 0
      ? row.destinationRoleId
      : undefined) ?? opts?.destinationRoleId;
  if (typeof destinationRoleId !== 'string' || destinationRoleId.length === 0) {
    return undefined;
  }

  const rawList =
    Array.isArray(row.mappings) && row.mappings.length > 0
      ? (row.mappings as Array<Record<string, unknown>>)
      : [row];

  return {
    destinationRoleId,
    selectedRoleMappingItems: rawList.map(normalizeRoleMappingItemFromApi),
  };
}

export function roleMappingItemsFromSelectedKeys(
  selectedKeys: string[],
  rows: RoleMappingSourceRow[],
): Array<Record<string, unknown>> {
  return selectedKeys.map((key) => {
    const mapping = rows.find((m) => m.id === key);
    const [companyId, roleId] = key.split('....');
    return {
      sourceCompanyId: companyId,
      sourceRoleId: roleId,
      sourceCompanyName: mapping?.name,
      sourceRoleName: mapping?.roleName,
    };
  });
}

export async function fetchCustomerSuccessRoleMappingRows(): Promise<RoleMappingSourceRow[]> {
  const data = await safeApiCall(() =>
    CustomerSuccessOptionsAppserviceService.getApiCustomerApiCustomerCustomerSuccessOptionsRoleMapping(),
  );
  return (data ?? []).map((mapping) => ({
    id: `${mapping.id}....${mapping.roleId}`,
    companyId: mapping.id ?? '',
    roleId: mapping.roleId ?? '',
    name: mapping.name ?? '',
    roleName: mapping.roleName ?? '',
  }));
}

function coerceCompanyMappingId(value: unknown): string | null {
  if (typeof value === 'string' && value.length > 0) return value;
  if (typeof value === 'number' && !Number.isNaN(value)) return String(value);
  return null;
}

/** Company mapping list row → compose / form hydrate shape (API DTO field names). */
export function buildCompanyMappingFormDataFromRow(
  row?: Record<string, unknown>,
): { sourceCompanyId: string; destinationCompanyId: string } | null {
  if (!row) return null;
  const sourceCompanyId = coerceCompanyMappingId(row.sourceCompanyId);
  const destinationCompanyId = coerceCompanyMappingId(row.destinationCompanyId);
  if (!sourceCompanyId || !destinationCompanyId) return null;
  return { sourceCompanyId, destinationCompanyId };
}

/** ComposeModal / executeBuilder passes ids via `params`, not `row`. */
export function buildCompanyMappingFormDataFromComposeParams(
  params: Record<string, unknown> | undefined,
): { sourceCompanyId: string; destinationCompanyId: string } | null {
  if (!params) return null;
  const sourceCompanyId = coerceCompanyMappingId(params.sourceCompanyId);
  const destinationCompanyId = coerceCompanyMappingId(params.destinationCompanyId);
  if (!sourceCompanyId || !destinationCompanyId) return null;
  return { sourceCompanyId, destinationCompanyId };
}

/** Prefer table row when present; fall back to compose params (ControlRenderer → executeBuilder). */
export function resolveCompanyMappingFormData(
  row: Record<string, unknown> | undefined,
  params: Record<string, unknown> | undefined,
): { sourceCompanyId: string; destinationCompanyId: string } | null {
  return (
    buildCompanyMappingFormDataFromRow(row) ??
    buildCompanyMappingFormDataFromComposeParams(params)
  );
}
